import { ActionIcon, Text, Tooltip, Popover, Button } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useMedia } from '@mygpt/app/utils';
import { useMatch } from 'react-router-dom';
import { useFeedbackButtonStore } from '@mygpt/app/chat/components/ChatContentBottomButton/store';
import { useHover } from '@mantine/hooks';
import { useBot } from '@mygpt/app/bot';
import { transferToHumanAction } from '@mygpt/app/chat/services';
import { modals } from '@mantine/modals';
import ReadOnlyFeedbackContentEditor from '@mygpt/app/chat/components/FeedbackContentEditor';
import { MouseEventHandler } from 'react';
import { useChatStore } from '@mygpt/app/chat/stores/useChatStore';

const GRAY_COLOR = '#757575';
const PURPLE_COLOR = '#6F5EE2';

const SVGFeedback = ({
  color = GRAY_COLOR,
  size = 24,
  className,
}: {
  color?: string;
  size?: number;
  className?: string;
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    className={className}
  >
    <path
      d="M20 2H4.01C2.91 2 2.01 2.9 2.01 4L2.00067 20.7919C2.00042 21.2375 2.53915 21.4609 2.85422 21.1458L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 15.6C20 15.8209 19.8209 16 19.6 16H5.33569C5.2296 16 5.12786 16.0421 5.05284 16.1172L4.34142 16.8286C4.21543 16.9546 4 16.8653 4 16.6872V4.4C4 4.17909 4.17909 4 4.4 4H19.6C19.8209 4 20 4.17909 20 4.4V15.6ZM12 10C13.1 10 14 9.1 14 8C14 6.9 13.1 6 12 6C10.9 6 10 6.9 10 8C10 9.1 10.9 10 12 10ZM16 13.43C16 12.62 15.52 11.9 14.78 11.58C13.93 11.21 12.99 11 12 11C11.01 11 10.07 11.21 9.22 11.58C8.48 11.9 8 12.62 8 13.43V14H16V13.43Z"
      fill={color}
    />
  </svg>
);

interface TransferToHumanButtonProps {
  messageId: string;
}

/**
 * 咨询负责人按钮
 * 这里的业务需求比较特别。
 *
 * 底部按钮的样式：
 * 1. PC、小部件相同
 * 2. 分享页面移动端自己相同
 *
 * 转人工弹窗的样式：
 * 1. PC端自己相同，是Popover
 * 2. 分享页移动端和小部件相同，是Modal
 */
export const TransferToHumanButton: React.FC<TransferToHumanButtonProps> = ({ messageId }) => {
  const { t, i18n } = useTranslation();
  const { isMobile } = useMedia();
  const match = useMatch('/widget/:botId');
  const isWidgetPage = !!match;
  const { alertMap } = useFeedbackButtonStore();
  const { hovered, ref } = useHover();
  const { robotInfo } = useBot();

  const feedbackForm = robotInfo?.configs?.feedback_form;
  const feedbackFormContent = feedbackForm?.content;
  const forwardChatHistory = feedbackForm?.forward_chat_history;

  // 开关关闭时的原有行为：点击链接上报转人工
  const handleTransferToHuman: MouseEventHandler<HTMLDivElement> = (e) => {
    if (!messageId || !robotInfo) return;
    if ((e.target as HTMLElement).tagName.toLowerCase() !== 'a') return;
    transferToHumanAction({ ai_id: robotInfo.id, message_id: messageId });
  };

  // 开关开启时：直接在当前聊天发送消息
  const handleDirectSend = () => {
    if (!messageId || !robotInfo) return;
    const aiInstruction = feedbackForm?.forward_chat_history_ai_instruction;
    if (!aiInstruction) return;

    transferToHumanAction({ ai_id: robotInfo.id, message_id: messageId }).catch(() => {});

    const { submitMessage } = useChatStore.getState();
    if (submitMessage) {
      const vt = feedbackForm?.forward_chat_history_visible_text;
      // 兼容旧格式（string）和新格式（Record<string, string>）
      const visibleText =
        typeof vt === 'string' ? vt : vt?.[i18n.language] || vt?.['en'] || (vt ? Object.values(vt)[0] : undefined);
      submitMessage(aiInstruction, visibleText);
    }
  };

  // 按钮点击处理：根据开关状态走不同逻辑
  const handleButtonClick = () => {
    if (forwardChatHistory) {
      handleDirectSend();
    } else {
      openFeedbackModal();
    }
  };

  const openFeedbackModal = () => {
    modals.open({
      centered: true,
      title: t('settings.feedback.name'),
      children: (
        <div onClick={handleTransferToHuman}>
          <ReadOnlyFeedbackContentEditor value={feedbackFormContent} />
        </div>
      ),
    });
  };

  if (isMobile && isWidgetPage) {
    return (
      <ActionIcon variant={'subtle'} color="indigo" onClick={handleButtonClick}>
        <Tooltip
          label={t('settings.feedback.name')}
          radius={999}
          withArrow
          opened={hovered || alertMap[messageId]}
          styles={{
            tooltip: {
              background: 'linear-gradient(135deg, #32A1EC 0%, #9145E3 100%)',
              paddingLeft: '12px',
              paddingRight: '12px',
            },
            arrow: {
              background: 'inherit',
            },
          }}
        >
          <span ref={ref}>
            <SVGFeedback color={alertMap[messageId] ? PURPLE_COLOR : GRAY_COLOR} />
          </span>
        </Tooltip>
      </ActionIcon>
    );
  }

  if (isMobile && !isWidgetPage) {
    return (
      <div className="flex flex-col items-center flex-1">
        <Button
          size="sm"
          variant="default"
          className="border-[#E4E7EC] rounded-md h-10 px-2 w-full flex items-center justify-center gap-1"
          styles={{
            root: {
              borderColor: alertMap[messageId] ? PURPLE_COLOR : '#E4E7EC',
            },
          }}
          ref={ref as any}
          onClick={handleButtonClick}
        >
          <SVGFeedback
            className="mr-1 inline-block mt-0.5"
            size={18}
            color={alertMap[messageId] ? PURPLE_COLOR : GRAY_COLOR}
          />
          <Text size="xs" c="gray.6">
            {t('settings.feedback.name')}
          </Text>
        </Button>
      </div>
    );
  }

  // 开关开启时：桌面端也直接发送，不弹 Popover
  if (forwardChatHistory) {
    return (
      <ActionIcon variant={'subtle'} color="indigo" onClick={handleDirectSend}>
        <Tooltip
          label={t('settings.feedback.name')}
          radius={999}
          withArrow
          opened={hovered || alertMap[messageId]}
          styles={{
            tooltip: {
              background: 'linear-gradient(135deg, #32A1EC 0%, #9145E3 100%)',
              paddingLeft: '12px',
              paddingRight: '12px',
            },
            arrow: {
              background: 'inherit',
            },
          }}
        >
          <span ref={ref}>
            <SVGFeedback color={alertMap[messageId] ? PURPLE_COLOR : GRAY_COLOR} />
          </span>
        </Tooltip>
      </ActionIcon>
    );
  }

  return (
    <Popover position="top" offset={13}>
      <Popover.Target>
        <ActionIcon variant={'subtle'} color="indigo">
          <Tooltip
            label={t('settings.feedback.name')}
            radius={999}
            withArrow
            opened={hovered || alertMap[messageId]}
            styles={{
              tooltip: {
                background: 'linear-gradient(135deg, #32A1EC 0%, #9145E3 100%)',
                paddingLeft: '12px',
                paddingRight: '12px',
              },
              arrow: {
                background: 'inherit',
              },
            }}
          >
            <span ref={ref}>
              <SVGFeedback color={alertMap[messageId] ? PURPLE_COLOR : GRAY_COLOR} />
            </span>
          </Tooltip>
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown
        styles={{
          dropdown: {
            width: 'fit-content',
            maxWidth: '400px',
            maxHeight: '500px',
            overflow: 'auto',
          },
        }}
      >
        <div onClick={handleTransferToHuman}>
          <ReadOnlyFeedbackContentEditor value={feedbackFormContent} />
        </div>
      </Popover.Dropdown>
    </Popover>
  );
};
